import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Box from 'components/UI/Box/Box';
import { formatCurrency } from 'utils/formatCurrency';
import { ReactComponent as Circle } from 'assets/icons/circle.svg';
import { ReactComponent as CircleDot } from 'assets/icons/circle-dot.svg';
import { ReactComponent as IconAlertTriangle } from 'assets/icons/alert-triangle.svg';
import Switch from 'components/UI/Switch';
import { SECTION_PAYMENT_OPTIONS_KEYS } from 'constants/global';
import ErrorModal from 'components/FeedbackModals/ErrorModal';
import { translate } from 'utils/translate';

const ContractPaymentOptions = ({
  options: optionsProp,
  label = '',
  className = '',
  literals,
  coverage = null,
  emptyOption = false,
  selectedPayment = '',
  selectedCoverage = null,
  onSelectPayment,
  onChangeCoverage = () => {},
}) => {
  const options = useMemo(() => (
    [
      ...(emptyOption ? [{ key: '0', title: literals.noPaymentOption }] : []),
      ...optionsProp,
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [optionsProp, emptyOption, literals]);
  const [showCoverageModal, setShowCoverageModal] = useState(false);

  const coverageDisabled = useMemo(() => {
    if (!coverage) {
      return false;
    }
    if (coverage.unavailable) {
      return translate(literals.payCoverageInShop, { amount: formatCurrency(coverage.balance) });
    }
    if (selectedPayment === SECTION_PAYMENT_OPTIONS_KEYS.EXTENSION) {
      return literals.disabledCoverageOnThisOption;
    }
    return false;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coverage, selectedPayment]);

  const handleSelectPayment = (key) => {
    onSelectPayment(key);
    if (key === SECTION_PAYMENT_OPTIONS_KEYS.EXTENSION) {
      onChangeCoverage(null);
    }
  };

  const handleChangeCoverage = (checked, e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!selectedPayment) {
      setShowCoverageModal(true);
      return;
    }
    onChangeCoverage(checked ? coverage.id : null);
  };

  const today = new Date().getTime();
  return (
    <div className={`mb-6 ${className}`}>
      {label !== false && <p className='mb-4 font-bold'>{label || literals.selectPaymentOption}</p>}
      {options.map((option) => (
        <Box key={option.key} className='mb-2 pt-2 pb-2 pe-2' padding='xs' onClick={() => handleSelectPayment(option.key)}>
          <div className='flex items-center'>
            <div style={{ width: 29 }}>
              {selectedPayment === option.key ? (
                <CircleDot width={29} height={29} />
              ) : (
                <Circle stroke='#CCCCCC' width={29} height={29} />
              )}
            </div>
            <div className='grow ps-2'>
              <p className='font-bold mb-0'>{option.title}</p>
              {option.key !== '0' ? (
                <>
                  {option.description && <p className='text-sm mt-2 mb-0'>{option.description}</p>}
                  <div className='flex justify-between mt-1'>
                    <div>
                      <p
                        className='text-sm mb-1 text-dark-gray font-bold'
                      >
                        {literals.common.amount}
                      </p>
                      <p className='mb-0'>{formatCurrency(option.amount)}</p>
                    </div>
                    <div>
                      <p
                        className='text-sm mb-1 text-dark-gray font-bold'
                      >
                        {literals.limitDate}
                      </p>
                      <div className='flex items-center gap-0'>
                        {option.limitDateTime < today ? <IconAlertTriangle height={16} /> : null }
                        <p className='mb-0'>{option.limitDate}</p>
                      </div>
                    </div>
                  </div>
                  {option.fechaResultante && (
                    <div className='mt-1'>
                      <p
                        className='text-sm mb-1 text-dark-gray font-bold'
                      >
                        {literals.newPaymentDate}
                      </p>
                      <p className='mb-0'>{option.fechaResultante}</p>
                    </div>
                  )}
                </>
              ) : null}
            </div>
          </div>
        </Box>
      ))}
      {coverage && (
        <Box className='mb-2 pt-2 pb-2 pe-2' padding='xs' onClick={(e) => !coverageDisabled && handleChangeCoverage(!selectedCoverage, e)}>
          <div className='flex items-center gap-2'>
            <Switch id='pay-coverage' checked={!!(selectedCoverage && !coverageDisabled)} disabled={coverageDisabled} />
            <div className='grow'>
              <p className='font-bold mb-0'>{literals.payCoverage}</p>
              {coverageDisabled ? (
                <p className='text-neutral-500 text-sm mb-0 mt-2'>{coverageDisabled}</p>
              ) : (
                <div className='flex justify-between mt-1'>
                  <div>
                    <p className='text-neutral-500 text-sm mb-1'>{literals.common.minAmount}</p>
                    <p className='mb-0'>{formatCurrency(coverage.min)}</p>
                  </div>
                  <div>
                    <p className='text-neutral-500 text-sm mb-1'>{literals.common.maxAmount}</p>
                    <p className='mb-0'>{formatCurrency(coverage.limit)}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Box>
      )}
      {showCoverageModal && (
        <ErrorModal
          title={literals.common.error}
          description={literals.cannotPayCoverageWithPaymentOption}
          onClose={() => setShowCoverageModal(false)}
        />
      )}
    </div>
  );
};

ContractPaymentOptions.propTypes = {
  options: PropTypes.array.isRequired,
  literals: PropTypes.object.isRequired,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  className: PropTypes.string,
  emptyOption: PropTypes.bool,
  coverage: PropTypes.object,
  selectedCoverage: PropTypes.number,
  selectedPayment: PropTypes.string,
  onSelectPayment: PropTypes.func.isRequired,
  onChangeCoverage: PropTypes.func,
};

export default ContractPaymentOptions;
